import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { drainSse, toDelta, StreamError } from 'src/lib/livyStream';
import { livyFetch } from 'src/lib/livyTransport';

/** Page context the drawer attaches so "why is this wrong?" resolves without the user naming anything. */

/**
 * All Livy chat state. Both shells, the full page and the drawer, are thin consumers of this,
 * which is why page-vs-drawer is one engine rather than two implementations.
 */
export function useLivyChat(_ref) {
  let config = _ref.config,
    getToken = _ref.getToken,
    context = _ref.context,
    _ref$surface = _ref.surface,
    surface = _ref$surface === void 0 ? 'admin' : _ref$surface;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    messages = _useState2[0],
    setMessages = _useState2[1];
  // One id per conversation ties every logged turn, tool call and transcript row together.
  const _useState3 = useState(() => crypto.randomUUID()),
    _useState4 = _slicedToArray(_useState3, 2),
    conversationId = _useState4[0],
    setConversationId = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    streaming = _useState6[0],
    setStreaming = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    error = _useState8[0],
    setError = _useState8[1];
  const abortRef = useRef(null);
  const stop = useCallback(() => {
    var _abortRef$current;
    return (_abortRef$current = abortRef.current) === null || _abortRef$current === void 0 ? void 0 : _abortRef$current.abort();
  }, []);

  // A turn outlives the component that started it unless someone aborts it. Closing the drawer or
  // switching surface mid-stream left the fetch running and its reader still calling setState, so
  // the tokens of an answer nobody can see were still being parsed and applied. Keyed on surface
  // as well as unmount: the surface is part of the request, so a stream started under the old one
  // is answering a prompt the user has already navigated away from.
  useEffect(() => () => {
    var _abortRef$current2;
    (_abortRef$current2 = abortRef.current) === null || _abortRef$current2 === void 0 || _abortRef$current2.abort();
    abortRef.current = null;
  }, [surface]);
  const send = useCallback(
  // `base` lets retry rebuild from the truncated history. Without it the send closed over the
  // pre-update messages array, so a retry re-sent the answer it was meant to replace.
  async (text, base) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    setError(null);
    setStreaming(true);
    const history = [...(base !== null && base !== void 0 ? base : messages), {
      role: 'user',
      content: trimmed
    }];
    setMessages([...history, {
      role: 'assistant',
      content: '',
      tools: [],
      requestIds: []
    }]);
    const controller = new AbortController();
    abortRef.current = controller;
    const patchLast = fn => setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? fn(m) : m));
    try {
      const token = await getToken();
      if (!token) throw new StreamError('Your session expired. Sign in again.');
      const res = await livyFetch(config, token, {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          surface,
          context,
          messages: history.map(_ref2 => {
            let role = _ref2.role,
              content = _ref2.content;
            return {
              role,
              content
            };
          })
        }),
        signal: controller.signal
      });
      if (!res.ok || !res.body) throw new StreamError('Livy is unavailable right now.');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const seen = {
        complete: false
      };
      for (;;) {
        const _await$reader$read = await reader.read(),
          done = _await$reader$read.done,
          value = _await$reader$read.value;
        if (done) break;
        buffer = drainSse(buffer + decoder.decode(value, {
          stream: true
        }), (event, data) => {
          const delta = toDelta(event, data);
          if (!delta) return;
          if (delta.done) seen.complete = true;
          if (delta.text) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            content: m.content + delta.text
          }));
          if (delta.toolName) {
            patchLast(m => {
              var _m$tools;
              return _objectSpread(_objectSpread({}, m), {}, {
                tools: [...((_m$tools = m.tools) !== null && _m$tools !== void 0 ? _m$tools : []), delta.toolName]
              });
            });
          }
          if (delta.requestId) {
            patchLast(m => {
              var _m$requestIds;
              return _objectSpread(_objectSpread({}, m), {}, {
                requestIds: [...new Set([...((_m$requestIds = m.requestIds) !== null && _m$requestIds !== void 0 ? _m$requestIds : []), delta.requestId])]
              });
            });
          }
        });
      }
      // A stream cut short by the server or something in between ends without error, so the
      // partial answer would otherwise be presented as finished and offered for copy, retry and
      // rating. Only message_stop means the answer is whole.
      if (!seen.complete) throw new StreamError('That answer was cut off. Try again.');
    } catch (e) {
      // An aborted turn is the user pressing stop, not a failure.
      if ((e === null || e === void 0 ? void 0 : e.name) !== 'AbortError') {
        // A generic failure here is unactionable without the cause: the transport can throw from
        // the credential exchange or from signing, and neither reaches the user-facing message.
        if (!(e instanceof StreamError)) console.error('[Livy] turn failed', e);
        setError(e instanceof StreamError ? e.message : 'Something went wrong.');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [config, getToken, context, surface, conversationId, messages, streaming]);
  const reset = useCallback(() => {
    setMessages([]);
    setConversationId(crypto.randomUUID());
  }, []);

  /**
   * Reopens a past conversation from the transcript tier. The turn rows are dropped: they are
   * operational metadata, and replaying them as messages would put latency numbers in the thread.
   */
  const resume = useCallback((id, history) => {
    setConversationId(id);
    setMessages(history);
    setError(null);
  }, []);

  /**
   * Re-asks the last question. The failed or unwanted answer is dropped first, so a retry replaces
   * it rather than stacking a second attempt underneath the first.
   */
  const retry = useCallback(() => {
    if (streaming) return;
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    const upTo = messages.lastIndexOf(lastUser);
    const truncated = messages.slice(0, upTo);
    setMessages(truncated);
    void send(lastUser.content, truncated);
  }, [messages, send, streaming]);
  return {
    messages,
    streaming,
    error,
    send,
    stop,
    reset,
    retry,
    resume,
    conversationId
  };
}